// Componente AddStockModal - Modal para agregar stock a un producto
// Permite seleccionar depósito y cargar stock para cada variante

import { useState, useEffect, useMemo } from 'react'
import Modal from '../Modal'
import Button from '../Button'
import Toast from '../Toast'
import { getAllWarehouses } from '../../services/warehouses.service'
import { setStock, getStock } from '../../services/stock.service'
import './AddStockModal.css'

const buildVariantLabel = (variant, index) => {
  const selection = variant?.selection || {}
  const color = selection.colorLabel || selection.color || selection.colorHex || selection.colorValue || ''
  const size = selection.size || selection.talle || selection.talla || ''
  const label = [color, size].filter(Boolean).join(' | ')
  if (label) return label
  if (variant?.sku) return `SKU ${variant.sku}`
  return `Variante ${index + 1}`
}

const getVariantSelection = (variant) => {
  const selection = variant?.selection || {}
  return {
    color: selection.color || selection.colorHex || selection.colorValue || selection.hex || null,
    size: selection.size || selection.talle || selection.talla || null
  }
}

const getColorDisplay = (variant, product) => {
  const selection = variant?.selection || {}
  const colorValue = selection.color || selection.colorHex || selection.colorValue || selection.hex || null

  if (!colorValue) return '-'

  if (selection.colorLabel && typeof selection.colorLabel === 'string' && selection.colorLabel.trim()) {
    return selection.colorLabel.trim()
  }

  if (product?.colorNames && typeof product.colorNames === 'object') {
    const nameFromMap = product.colorNames[colorValue]
    if (nameFromMap && typeof nameFromMap === 'string' && nameFromMap.trim()) {
      return nameFromMap.trim()
    }
  }

  return colorValue
}

function AddStockModal({ isOpen, onClose, product, onStockAdded }) {
  const [warehouses, setWarehouses] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  const [selectedWarehouse, setSelectedWarehouse] = useState('')
  const [variantStocks, setVariantStocks] = useState({})
  const [variantQuantities, setVariantQuantities] = useState({})
  const [productStock, setProductStock] = useState(null)

  const variantOptions = useMemo(() => {
    if (!product) return []
    const variants = Array.isArray(product?.variants) ? product.variants : []
    return variants
      .map((variant, index) => {
        const selection = getVariantSelection(variant)
        if (!selection.color && !selection.size) return null
        return {
          index,
          label: buildVariantLabel(variant, index),
          variant,
          selection
        }
      })
      .filter(Boolean)
  }, [product])

  const hasVariants = variantOptions.length > 0

  useEffect(() => {
    if (isOpen && product) {
      loadWarehouses()
      setVariantStocks({})
      setVariantQuantities({})
      setSelectedWarehouse('')
    }
  }, [isOpen, product])

  const loadWarehouses = async () => {
    try {
      setLoading(true)
      const data = await getAllWarehouses()
      setWarehouses(data)
      if (data.length === 1) {
        setSelectedWarehouse(data[0].id)
      }
    } catch (err) {
      console.error('Error cargando depósitos:', err)
      setNotification({ type: 'error', message: 'Error al cargar los depósitos' })
    } finally {
      setLoading(false)
    }
  }

  const loadProductStock = async () => {
    if (!product?.id || !selectedWarehouse) {
      setProductStock(null)
      return
    }
    try {
      const stock = await getStock(product.id, selectedWarehouse)
      setProductStock(stock?.quantity || 0)
    } catch (err) {
      console.error('Error cargando stock:', err)
      setProductStock(0)
    }
  }

  useEffect(() => {
    if (selectedWarehouse && product?.id) {
      loadProductStockForVariants()
    }
  }, [selectedWarehouse, product])

  const loadProductStockForVariants = async () => {
    if (!product?.id || !selectedWarehouse) return
    const stockMap = {}
    for (const v of variantOptions) {
      try {
        const stock = await getStock(product.id, selectedWarehouse, {
          size: v.selection.size || undefined,
          color: v.selection.color || undefined
        })
        stockMap[v.index] = stock?.quantity || 0
      } catch (err) {
        stockMap[v.index] = 0
      }
    }
    setVariantStocks(stockMap)
  }

  const handleQuantityChange = (index, value) => {
    const num = parseInt(value) || 0
    setVariantQuantities(prev => ({ ...prev, [index]: num }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!selectedWarehouse) {
      setNotification({ type: 'error', message: 'Debes seleccionar un depósito' })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (hasVariants) {
      const hasQuantity = Object.values(variantQuantities).some(q => q > 0)
      if (!hasQuantity) {
        setNotification({ type: 'error', message: 'Debes ingresar al menos una cantidad mayor a 0' })
        setTimeout(() => setNotification(null), 3000)
        return
      }
    } else {
      const qty = parseInt(variantQuantities.simple || 0)
      if (!qty || qty <= 0) {
        setNotification({ type: 'error', message: 'Debes ingresar una cantidad mayor a 0' })
        setTimeout(() => setNotification(null), 3000)
        return
      }
    }

    setSaving(true)

    try {
      if (hasVariants) {
        for (const v of variantOptions) {
          const addQuantity = variantQuantities[v.index] || 0
          if (addQuantity > 0) {
            const currentQty = variantStocks[v.index] || 0
            const newQty = currentQty + addQuantity
            await setStock(product.id, selectedWarehouse, newQty, null, {
              size: v.selection.size || null,
              color: v.selection.color || null
            })
          }
        }
      } else {
        const addQuantity = parseInt(variantQuantities.simple || 0)
        if (addQuantity > 0) {
          const currentQty = productStock || 0
          const newQty = currentQty + addQuantity
          await setStock(product.id, selectedWarehouse, newQty, null, {})
        }
      }

      setNotification({ type: 'success', message: 'Stock agregado correctamente' })
      setTimeout(() => {
        setNotification(null)
        if (onStockAdded) onStockAdded()
        onClose()
      }, 1500)
    } catch (err) {
      console.error('Error guardando stock:', err)
      setNotification({ type: 'error', message: `Error al guardar: ${err.message || 'Error desconocido'}` })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  if (!product) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Agregar Stock: ${product.name || 'Producto'}`}
      size="large"
    >
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <form onSubmit={handleSubmit} className="add-stock-form">
        <div className="form-group">
          <label htmlFor="warehouse">
            Depósito <span className="required">*</span>
          </label>
          <select
            id="warehouse"
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            required
            disabled={loading || saving}
            className="form-input"
          >
            <option value="">Seleccionar depósito...</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>

        <div className="divider"></div>

        {loading ? (
          <div className="loading-state">
            <p>Cargando depósitos...</p>
          </div>
        ) : hasVariants ? (
          <div className="variants-section">
            <h4>Variantes del Producto</h4>
            <div className="variants-table-container">
              <div className="variants-table">
                <div className="variants-table-header">
                  <span>Talle</span>
                  <span>Color</span>
                  <span>Stock Actual</span>
                  <span>Nueva cantidad</span>
                </div>
                {variantOptions.map((v) => {
                  const selection = v.selection
                  const currentStock = variantStocks[v.index] !== undefined ? variantStocks[v.index] : '...'
                  const addQuantity = variantQuantities[v.index] !== undefined ? variantQuantities[v.index] : ''
                  return (
                    <div key={v.index} className="variant-row">
                      <span className="variant-size">
                        {selection.size || '-'}
                      </span>
                      <span className="variant-color">
                        {selection.color ? (
                          <div className="color-indicator">
                            <span className="color-dot" style={{ backgroundColor: selection.color }}></span>
                            <span className="color-text">{getColorDisplay(v.variant, product)}</span>
                          </div>
                        ) : '-'}
                      </span>
                      <span className="variant-current">
                        {currentStock}
                      </span>
                      <span className="variant-input">
                        <input
                          type="number"
                          value={addQuantity}
                          onChange={(e) => handleQuantityChange(v.index, e.target.value)}
                          placeholder="0"
                          min="0"
                          step="1"
                          disabled={saving}
                          className="quantity-input"
                        />
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="simple-stock-section">
            <h4>Stock del Producto</h4>
            <div className="simple-stock-row">
              <div className="current-stock">
                <label>Stock Actual:</label>
                <span className="stock-value">{productStock ?? '-'}</span>
              </div>
              <div className="new-stock">
                <label htmlFor="simpleQuantity">Nueva cantidad:</label>
                <input
                  type="number"
                  id="simpleQuantity"
                  value={variantQuantities.simple || ''}
                  onChange={(e) => setVariantQuantities({ simple: e.target.value })}
                  placeholder="0"
                  min="0"
                  step="1"
                  disabled={saving}
                  className="quantity-input"
                />
              </div>
            </div>
          </div>
        )}

        <div className="divider"></div>

        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !selectedWarehouse}
          >
            {saving ? 'Guardando...' : 'Agregar Stock'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default AddStockModal
